<template>
  <div class="package-detail-panel">
    <div class="package-detail-heading">
      <strong>{{ detail.packCode || '—' }}</strong>
      <span>{{ detail.packName || '—' }}</span>
      <span
        class="package-period-tag"
        :class="{ 'not-available': !hasResult }"
      >
        {{ finalCycle }}
      </span>
    </div>

    <section>
      <h3>人工配置优先级</h3>
      <dl class="package-rule-overview">
        <div>
          <dt>规则优先级</dt>
          <dd>
            <span
              class="package-priority"
            >
              {{ priorityText }}
            </span>
          </dd>
        </div>
        <div>
          <dt>适用范围</dt>
          <dd>{{ applicableScope }}</dd>
        </div>
      </dl>
    </section>

    <section>
      <h3>双发货方式结果</h3>
      <div class="package-dual-periods">
        <div>
          <span>活动快递发货周期</span>
          <span
            class="package-period-tag"
            :class="{ 'not-available': !hasCycle(expressCycle) }"
          >
            {{ expressCycle }}
          </span>
        </div>
        <div>
          <span>活动物流发货周期</span>
          <span
            class="package-period-tag"
            :class="{ 'not-available': !hasCycle(logisticsCycle) }"
          >
            {{ logisticsCycle }}
          </span>
        </div>
        <div>
          <span>是否满足7天发货</span>
          <b
            class="package-seven-day"
            :class="{
              'is-yes': detail.sevenDayFlag === '是',
              'is-no': detail.sevenDayFlag === '否'
            }"
          >
            {{ detail.sevenDayFlag || '—' }}
          </b>
        </div>
      </div>
    </section>

    <section>
      <h3>匹配判断</h3>
      <div class="package-reason">
        <p
          v-for="(reason, index) in matchingReasons"
          :key="index"
        >
          {{ reason }}
        </p>
      </div>
    </section>

    <section>
      <h3>参与计算的数据</h3>
      <table class="package-input-table spec-dialog-table">
        <thead>
          <tr>
            <th>字段</th>
            <th>当前版本取值</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(field, index) in inputRows"
            :key="index"
          >
            <td>{{ field.fieldName }}</td>
            <td :title="String(field.value)">
              {{ field.value }}
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="package-result">
      <span>最终活动发货周期</span>
      <span
        class="package-period-tag"
        :class="{ 'not-available': !hasResult }"
      >
        {{ finalCycle }}
      </span>
      <small>计算时间：{{ detail.calculateTime || '—' }}</small>
    </section>
  </div>
</template>

<script>
import { hasCycle, packageText, periodText } from '../model'

export default {
  name: 'ActivityPackagePeriodDetail',
  props: {
    detail: {
      type: Object,
      required: true
    }
  },
  computed: {
    ruleHits () {
      return this.detail.ruleHits || []
    },
    priorityText () {

      const priorities = this.ruleHits
        .map(hit => hit.priorityCode)
        .filter(Boolean)

      return this.inputValue(['规则优先级']) ||
        [...new Set(priorities)].join(' / ') ||
        '—'
    },
    applicableScope () {
      return [
        this.detail.lifeCycleTypeName,
        this.detail.productionLogisticsTypeName,
        this.detail.supplyChannelName
      ].filter(Boolean).join(' / ') || '—'
    },
    finalCycle () {
      return packageText(this.detail)
    },
    hasResult () {
      return hasCycle(this.finalCycle) && this.finalCycle !== '不计算'
    },
    hideDualCycles () {
      return hasCycle(this.detail.regularDeliveryCycle) ||
        this.detail.exceptionCode === 'NOT_SHELF' ||
        this.detail.lifeCycleType === 'NOT_SHELF'
    },
    expressCycle () {
      return periodText(this.detail.expressDeliveryCycle)
    },
    logisticsCycle () {
      return periodText(this.detail.logisticsDeliveryCycle)
    },
    matchingReasons () {
      const reasons = []

      this.ruleHits.forEach(hit => {
        if (hit.ruleDesc) {
          reasons.push(hit.ruleDesc)
        }
      })

      return reasons.length ? [...new Set(reasons)] : ['暂无匹配判断明细']
    },
    inputRows () {
      const rows = []
      const existingNames = new Set(rows.map(row => row.fieldName))
      const additionalFields = (this.detail.inputFields || [])
        .filter(field => !existingNames.has(field.fieldName))
        .map(field => ({
          fieldName: field.fieldName,
          value: field.value == null || field.value === '' ? '—' : field.value
        }))

      return rows.concat(additionalFields)
    }
  },
  methods: {
    hasCycle,
    inputValue (names) {
      const field = (this.detail.inputFields || []).find(item => (
        names.includes(item.fieldName) && hasCycle(item.value)
      ))

      return field ? String(field.value) : ''
    }
  }
}
</script>

<style scoped>
.package-detail-panel {
  color: #303133;
  font-size: 14px;
  line-height: 1.6;
}

.package-detail-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.package-detail-heading strong {
  font-size: 16px;
}

section {
  margin-top: 20px;
}

h3 {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
}

.package-rule-overview {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: 16px;
  margin: 0;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
}

.package-rule-overview dt {
  margin-bottom: 8px;
  color: #909399;
}

.package-rule-overview dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.package-period-tag,
.package-priority {
  display: inline-block;
  padding: 2px 10px;
  color: #138f77;
  background: #eaf7f2;
  border: 1px solid #cdebe0;
  border-radius: 4px;
  font-weight: 600;
}

.package-period-tag.not-available {
  color: #909399;
  background: #f5f7fa;
  border-color: #e4e7ed;
}

.package-priority {
  color: #409eff;
  background: #ecf5ff;
  border-color: #d9ecff;
}

.package-priority.is-highest {
  color: #b88230;
  background: #fdf6ec;
  border-color: #faecd8;
}

.package-dual-periods {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.package-dual-periods > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.package-seven-day {
  color: #909399;
}

.package-seven-day.is-yes {
  color: #138f77;
}

.package-seven-day.is-no {
  color: #e6a23c;
}

.package-reason {
  padding: 14px 16px;
  background: #f5f7fa;
  border-radius: 4px;
  overflow-wrap: anywhere;
}

.package-reason p {
  margin: 0;
}

.package-reason p + p {
  margin-top: 8px;
}

.package-input-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.package-input-table th,
.package-input-table td {
  padding: 10px 16px;
  border: 1px solid #ebeef5;
  text-align: left;
  overflow-wrap: anywhere;
}

.package-input-table th {
  color: #606266;
  background: #f5f7fa;
  font-weight: 500;
}

.package-input-table th:first-child {
  width: 32%;
}

.package-result {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f0f9f5;
  border: 1px solid #d8eee4;
  border-radius: 4px;
}

.package-result > span:first-child {
  font-weight: 600;
}

.package-result small {
  margin-left: auto;
  color: #909399;
}
</style>
